import React, { useState, useEffect, useRef } from 'react';
import conversationAPI from '../../conversationApi';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  LinearProgress, 
  IconButton, 
  Snackbar, 
  Alert,
  Button,
  useTheme
} from '@mui/material';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import ConversationHeader from './ConversationHeader';
import MessageContainer from './MessageContainer';
import InputSection from './InputSection';
import HintPanel from './HintPanel';

const ConversationApp = ({ 
  sessionId, 
  templateId, 
  userId,
  userLanguage = 'en',
  onBack 
}) => {
  const theme = useTheme();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showHints, setShowHints] = useState(false);
  const [hints, setHints] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'info' });
  const [inputText, setInputText] = useState(''); // New state to store input text

  const messageEndRef = useRef(null);

  // Load conversation data
  useEffect(() => {
    const loadConversation = async () => {
      try {
        setLoading(true);
        
        if (sessionId) {
          // Load existing conversation
          const response = await conversationAPI.getSession(sessionId);
          setSession(response.data);
          
          // Add translations and hints to messages if they don't exist
          const processedMessages = response.data.messages.map(msg => {
            // Add missing fields for display compatibility
            return {
              ...msg,
              translation: msg.translation || null,
              hints: msg.hints || [],
              corrections: msg.corrections || []
            };
          });
          
          setMessages(processedMessages);
        } else if (templateId && userId) {
          // Start a new conversation
          const response = await conversationAPI.startConversation({
            userId,
            templateId
          });
          
          setSession(response.data);
          
          // Process initial message to ensure it has translation if available
          const initialMessages = response.data.messages.map(msg => ({
            ...msg,
            translation: msg.translation || null,
            hints: msg.hints || [],
            corrections: msg.corrections || []
          }));
          
          setMessages(initialMessages);
          
          // Auto-play initial message
          if (response.data.messages.length > 0) {
            const initialMessage = response.data.messages[0].content;
            speakText(initialMessage, response.data.language);
          }
          
          // Request translation for initial message if not present
          if (response.data.messages.length > 0 && !response.data.messages[0].translation) {
            requestTranslation(response.data.messages[0], response.data.language, userLanguage);
          }
        } else {
          throw new Error('Either sessionId or templateId and userId required');
        }
      } catch (err) {
        console.error('Error loading conversation:', err);
        setError(err.message || 'Failed to load conversation');
        showAlert('Failed to load conversation', 'error');
      } finally {
        setLoading(false);
      }
    };
    
    loadConversation();
  }, [sessionId, templateId, userId, userLanguage]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Request translation for a message
  const requestTranslation = async (message, fromLanguage, toLanguage) => {
    // This would be implemented to call a backend API for translation
    // For now, this is a placeholder
    console.log(`Translation requested for message: ${message.content}`);
  };

  // WebSpeech API for text-to-speech
  const speakText = (text, language) => {
    if (!text || !window.speechSynthesis) return;
    
    // Cancel any ongoing speech
    window.speechSynthesis.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = language;
    
    // Find appropriate voice
    const voices = window.speechSynthesis.getVoices();
    const voice = voices.find(v => v.lang.startsWith(language.split('-')[0]));
    if (voice) {
      utterance.voice = voice;
    }
    
    window.speechSynthesis.speak(utterance);
  };

  // Send user message with retry mechanism
  const sendMessage = async (content) => {
    if (!content.trim() || isProcessing) return;
    
    try {
      setIsProcessing(true);
      
      // Optimistically add user message to UI
      const tempUserMessage = {
        _id: Date.now().toString(),
        sender: 'user',
        content,
        translation: null,
        corrections: [],
        hints: [],
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, tempUserMessage]);
      
      // Show typing indicator
      setIsTyping(true);
      
      // Implement retry logic
      const MAX_RETRIES = 3;
      let lastError = null;
      let response = null;
      
      for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        try {
          response = await conversationAPI.sendMessage({
            sessionId: session._id,
            message: content,
            userLanguage
          });
          // If successful, break out of retry loop
          break;
        } catch (err) {
          lastError = err;
          console.warn(`Send message attempt ${attempt + 1} failed:`, err);
          
          // If this wasn't the last attempt, wait before retrying
          if (attempt < MAX_RETRIES - 1) {
            // Exponential backoff: 1s, 2s, 4s...
            const delay = Math.pow(2, attempt) * 1000;
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }
      
      // If all retries failed
      if (!response) {
        // Mark message as failed with resend option
        const failedMessage = {
          ...tempUserMessage,
          failed: true
        };
        
        setMessages(prev => prev.map(msg => 
          msg._id === tempUserMessage._id ? failedMessage : msg
        ));
        
        setIsTyping(false);
        setIsProcessing(false);
        
        showAlert('Failed to send message. Please try resending.', 'error');
        throw lastError;
      }
      
      // Hide typing indicator
      setIsTyping(false);
      
      // Process messages to ensure all have required fields
      const processedMessages = response.data.session.messages.map(msg => ({
        ...msg,
        translation: msg.translation || null,
        hints: msg.hints || [],
        corrections: msg.corrections || []
      }));
      
      // Update session with processed messages
      const updatedSession = {
        ...response.data.session,
        messages: processedMessages
      };
      
      setSession(updatedSession);
      setMessages(processedMessages);
      
      // Set hints if available
      if (response.data.hints && response.data.hints.length > 0) {
        setHints(response.data.hints);
      }
      
      // Auto-play the AI response
      const lastMessage = processedMessages[processedMessages.length - 1];
      if (lastMessage && lastMessage.sender === 'system') {
        speakText(lastMessage.content, session.language);
      }
      
      // Check if conversation is complete
      if (response.data.session.completed) {
        showAlert('Conversation completed successfully!', 'success');
      }
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle resending a failed message
  const handleResendMessage = (failedMessage) => {
    // Get the content of the failed message
    const messageContent = failedMessage.content;
    
    // Remove the failed message from the UI
    setMessages(prev => prev.filter(msg => msg._id !== failedMessage._id));
    
    // Resend the message
    sendMessage(messageContent);
  };

  // Speech recognition for voice input
  const startSpeechRecognition = (onResultCallback) => {
    if (!window.SpeechRecognition && !window.webkitSpeechRecognition) {
      showAlert('Speech recognition is not supported in your browser.', 'error');
      return null;
    }
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.lang = session.language;
    recognition.continuous = true; // Keep recording until manually stopped
    recognition.interimResults = true; // Get results as user speaks
    
    recognition.onresult = (event) => {
      // Get the latest transcript
      const resultIndex = event.results.length - 1;
      const transcript = event.results[resultIndex][0].transcript;
      
      // Update input field with transcript
      onResultCallback(transcript);
    };
    
    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      // Don't stop recording for no-speech errors to allow pauses
      if (event.error !== 'no-speech') {
        showAlert('Speech recognition error: ' + event.error, 'error');
      }
    };
    
    return recognition;
  };

  // Handle voice input
  const handleVoiceInput = (callback) => {
    const recognition = startSpeechRecognition(callback);
    if (recognition) {
      recognition.start();
      return recognition; // Return recognition instance for stopping later
    }
    return null;
  };

  // Set input text - used for suggestions
  const setInputTextValue = (text) => {
    setInputText(text);
  };

  // Toggle hints panel
  const toggleHints = () => {
    setShowHints(prev => !prev);
  };

  // Use a hint as input text (but don't send)
  const useHint = (hintText) => {
    setInputText(hintText); // Just set the input text
    // Keep hints panel open so user can try different suggestions
  };

  // Show alert message
  const showAlert = (message, severity = 'info') => {
    setAlert({
      show: true,
      message,
      severity
    });
  };

  // Handle alert close
  const handleAlertClose = () => {
    setAlert({ ...alert, show: false });
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ pt: 4, textAlign: 'center' }}>
        <Typography variant="h6" mb={2} color="primary.main">Loading conversation...</Typography>
        <LinearProgress color="primary" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ pt: 4 }}>
        <Paper elevation={3} sx={{ p: 3, textAlign: 'center', borderRadius: theme.shape.borderRadius * 2 }}>
          <Typography color="error" variant="h6" mb={2}>
            Error: {error}
          </Typography>
          <IconButton onClick={onBack} color="primary">
            <ArrowBackIcon /> Go Back
          </IconButton>
        </Paper>
      </Container>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100vh',
      bgcolor: 'background.default' 
    }}>
      {/* Conversation header */}
      <ConversationHeader 
        title={session.title}
        goal={session.goal}
        language={session.language}
        onBack={onBack}
      />
      
      {/* Messages area */}
      <Box sx={{ 
        flexGrow: 1, 
        overflow: 'auto', 
        px: { xs: 1.5, sm: 2, md: 3 },
        py: 2,
        position: 'relative',
        bgcolor: theme.palette.background.default
      }}>
        <MessageContainer 
          messages={messages}
          userLanguage={userLanguage}
          onPlayAudio={(text) => speakText(text, session.language)}
          onResend={handleResendMessage}
          isTyping={isTyping}
          onUseHint={setInputTextValue}
        />
        <div ref={messageEndRef} />
        
        {/* Hints panel */}
        {showHints && (
          <HintPanel 
            hints={hints}
            onUseHint={useHint}
            onClose={() => setShowHints(false)}
          />
        )}
      </Box>
      
      {/* Input area */}
      <InputSection 
        onSendMessage={sendMessage}
        onVoiceInput={handleVoiceInput}
        onToggleHints={toggleHints}
        showHints={showHints}
        isProcessing={isProcessing || isTyping}
        language={session.language}
        inputText={inputText}
        setInputText={setInputTextValue}
      />
      
      {/* Alert messages */}
      <Snackbar
        open={alert.show}
        autoHideDuration={6000}
        onClose={handleAlertClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleAlertClose} 
          severity={alert.severity} 
          elevation={6} 
          variant="filled"
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConversationApp;